import React, { useEffect, useState } from 'react';
import {
    Accordion, AccordionDetails, AccordionSummary, Alert, AlertTitle, Box, Button, Checkbox, CircularProgress, Dialog, DialogActions, DialogContent,
    DialogTitle, FormControlLabel, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { formatError } from '../../Mobx/AudioCatalog';
import {
    GENDER_LABELS, MODE_LABELS, PROVIDER_LABELS, backupFolderPattern, describeBlocker, describeVoices, describeWarning, formatClock, formatDuration, formatNumber,
    formatUsd, groupsFolderPattern, isReplaceMode, isTrialMode, languageLabel, modeOf, plannedFiles, trialFolderPattern, PATH_STYLE
} from './bulkAudioForm';

/** One number of the plan */
const Stat = ({ label, value, note, emphasis, testId }) => (
    <Box sx={{ border: 1, borderColor: emphasis ? 'primary.main' : 'divider', borderRadius: 1, p: 1.5, bgcolor: emphasis ? 'action.hover' : 'transparent' }}>
        <Typography variant="body2" color="text.secondary">{label}</Typography>
        <Typography variant={emphasis ? 'h5' : 'h6'} data-testid={testId}>{value}</Typography>
        {note && <Typography variant="caption" color="text.secondary">{note}</Typography>}
    </Box>
);

/** The place of the backups, as the plan says it (the server sends its prefix), before the run has an id */
const backupPatternOf = (plan, options) => backupFolderPattern({ backupPrefix: plan.replace?.backupPrefix ?? options?.backupPrefix });
/** The place of the files of a trial, as the plan says it, before the run has an id */
const trialPatternOf = (plan, options) => trialFolderPattern({ trialPrefix: plan.trial?.trialPrefix ?? options?.trialPrefix });

/**
 * The confirmation before a run starts: the numbers of the plan, once more, in a sentence.
 * A run that replaces files asks for one more thing: a tick that says that the existing files will be replaced.
 */
const StartDialog = ({ plan, options, open, starting, onCancel, onConfirm }) => {
    const replacing = isReplaceMode(modeOf(plan));
    const trial = isTrialMode(modeOf(plan));
    const [understood, setUnderstood] = useState(false);
    useEffect(() => {
        if (!open) setUnderstood(false);
    }, [open]);

    return (
        <Dialog open={open} onClose={starting ? undefined : onCancel} maxWidth="sm" fullWidth>
            <DialogTitle>{replacing ? 'להחליף את הקבצים הקיימים?' : (trial ? 'להתחיל את הניסוי?' : 'להתחיל את ההרצה?')}</DialogTitle>
            <DialogContent>
                {replacing ? (
                    <>
                        <Typography sx={{ mb: 1 }} data-testid="bulk-confirm-text">
                            יוחלפו <b>{formatNumber(plannedFiles(plan))}</b> קבצי אודיו שקיימים עכשיו ({formatNumber(plan.chars)} תווים) בקבצים חדשים, בעזרת {PROVIDER_LABELS[plan.provider] ?? plan.provider}.
                            העלות המשוערת {formatUsd(plan.estimatedCostUsd)}, ולא תעבור את התקרה: {formatUsd(plan.maxCostUsd)}.
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 1 }} data-testid="bulk-confirm-backup">
                            לפני כל החלפה הקובץ הישן מועתק לתיקיית הגיבוי של ההרצה (<bdi dir="ltr" style={PATH_STYLE}>{backupPatternOf(plan, options)}</bdi>) והעותק נבדק.
                            אם הגיבוי של קובץ נכשל, הקובץ לא מוחלף וההרצה מושהית. שום קובץ לא נמחק.
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            {plan.files.missing > 0 ? `${formatNumber(plan.files.missing)} קבצים שלא קיימים לא ייווצרו. ` : ''}
                            אפשר לעצור את ההרצה בכל רגע: מה שכבר הוחלף נשאר מוחלף, והגיבוי שלו נשמר.
                            אם מספר הקבצים שיוחלפו ישתנה לפני ההתחלה, ההרצה לא תתחיל ותוצג תוכנית חדשה.
                        </Typography>
                    </>
                ) : trial ? (
                    <>
                        <Typography sx={{ mb: 1 }} data-testid="bulk-confirm-text">
                            ייווצרו לניסוי <b>{formatNumber(plannedFiles(plan))}</b> קבצי אודיו ({formatNumber(plan.chars)} תווים) בעזרת {PROVIDER_LABELS[plan.provider] ?? plan.provider}.
                            העלות המשוערת {formatUsd(plan.estimatedCostUsd)}, ולא תעבור את התקרה: {formatUsd(plan.maxCostUsd)}.
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }} data-testid="bulk-confirm-trial">
                            הקבצים נכתבים לתיקייה משלהם (<bdi dir="ltr" style={PATH_STYLE}>{trialPatternOf(plan, options)}</bdi>) ולא לאתר, ושום קובץ שקיים לא ייגע.
                            אפשר לעצור את הניסוי בכל רגע. אם מספר הקבצים ישתנה לפני ההתחלה, הניסוי לא יתחיל ותוצג תוכנית חדשה.
                        </Typography>
                    </>
                ) : (
                    <>
                        <Typography sx={{ mb: 1 }} data-testid="bulk-confirm-text">
                            ייווצרו <b>{formatNumber(plannedFiles(plan))}</b> קבצי אודיו ({formatNumber(plan.chars)} תווים) בעזרת {PROVIDER_LABELS[plan.provider] ?? plan.provider}.
                            העלות המשוערת {formatUsd(plan.estimatedCostUsd)}, ולא תעבור את התקרה: {formatUsd(plan.maxCostUsd)}.
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            קבצים שכבר קיימים ({formatNumber(plan.files.existing)}) לא ייגעו. אפשר לעצור את ההרצה בכל רגע.
                            אם המצב ישתנה לפני ההתחלה (מספר הקבצים החסרים), ההרצה לא תתחיל ותוצג תוכנית חדשה.
                        </Typography>
                    </>
                )}
                {plan.group && (
                    <Typography variant="body2" sx={{ mb: 1 }} data-testid="bulk-confirm-group">
                        ההקראה בקבוצות: {formatNumber(plan.group.requests)} בקשות ל-Google, בכל אחת עד {formatNumber(plan.group.size)} מילים. ההקלטה המלאה של כל קבוצה
                        נשמרת בצד (פרטית) להאזנה.
                    </Typography>
                )}
                <Typography variant="body2" dir="auto">{describeVoices(plan.voices)}</Typography>
                {replacing && (
                    <FormControlLabel
                        sx={{ mt: 1 }}
                        control={<Checkbox checked={understood} onChange={() => setUnderstood(!understood)} disabled={starting} inputProps={{ 'data-testid': 'bulk-confirm-understood' }} />}
                        label="הבנתי: קבצים שקיימים באתר יוחלפו בקבצים חדשים, והדרך לחזור אחורה היא הגיבוי של ההרצה."
                    />
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onCancel} disabled={starting}>ביטול</Button>
                <Button
                    variant="contained"
                    color={replacing ? 'warning' : 'primary'}
                    onClick={onConfirm}
                    disabled={starting || (replacing && !understood)}
                    startIcon={starting ? <CircularProgress size={16} color="inherit" /> : null}
                    data-testid="bulk-confirm-start"
                >
                    {replacing ? 'התחלת ההחלפה' : (trial ? 'התחלת הניסוי' : 'התחלת הרצה')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

/**
 * The plan of a run (a dry run: nothing was created or replaced) and the start button.
 * The start is open only while the plan is the plan of the form (stale = false), nothing blocks it and no other run works.
 */
const BulkRunPlan = ({ plan, stale, options, starting, startError, activeJob, onStart, onContinueAfter }) => {
    const [confirming, setConfirming] = useState(false);
    if (!plan) return null;

    const mode = modeOf(plan);
    const replacing = isReplaceMode(mode);
    const trial = isTrialMode(mode);
    const files = plannedFiles(plan);
    const blocked = !plan.canRun;
    const disabledReason = (() => {
        if (activeJob) return 'הרצה אחרת פועלת עכשיו. אפשר להתחיל הרצה חדשה כשהיא מסתיימת או נעצרת.';
        if (stale) return 'הטופס השתנה מאז הבדיקה. צריך לבדוק תוכנית מחדש.';
        if (blocked) return 'יש חסימה בתוכנית (למעלה). עד שהיא נפתרת אי אפשר להתחיל.';
        return '';
    })();

    // The dialog closes when the answer is back: a refusal is shown under the plan (with the new plan, when the numbers changed)
    const confirm = async () => {
        await onStart();
        setConfirming(false);
    };

    return (
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }} data-testid="bulk-plan-card">
            <Typography variant="h6">{replacing ? 'תוכנית (בדיקה בלבד, לא שונה כלום)' : 'תוכנית (בדיקה בלבד, לא נוצר כלום)'}</Typography>
            <Typography variant="body2" color="text.secondary" data-testid="bulk-plan-mode">
                מצב: {MODE_LABELS[mode] ?? mode}{plan.group ? ` · בקבוצות של עד ${formatNumber(plan.group.size)} מילים` : ''}
            </Typography>

            {stale && (
                <Alert severity="info" sx={{ mt: 1 }} data-testid="bulk-plan-stale">
                    הטופס השתנה מאז שהתוכנית נבדקה, ולכן היא כבר לא בהכרח נכונה. לוחצים שוב על "בדיקת תוכנית".
                </Alert>
            )}

            {plan.blockers.map((blocker) => (
                <Alert key={`${blocker.code}-${blocker.message}`} severity="error" sx={{ mt: 1 }} data-testid={`bulk-blocker-${blocker.code}`}>
                    {describeBlocker(blocker, plan, options)}
                </Alert>
            ))}
            {plan.warnings.map((warning) => (
                <Alert key={`${warning.code}-${warning.message}`} severity="warning" sx={{ mt: 1 }} data-testid={`bulk-warning-${warning.code}`}>
                    {describeWarning(warning, mode)}
                </Alert>
            ))}
            {trial && (
                <Alert severity="info" sx={{ mt: 1 }} data-testid="bulk-trial-info">
                    הקבצים נכתבים אל <bdi dir="ltr" style={PATH_STYLE}>{trialPatternOf(plan, options)}</bdi> ולא אל קבצי האודיו של האתר, ולכן אף אחד לא שומע אותם באתר.
                    אחרי הניסוי מאזינים לקבצים ולהקלטות המלאות במסך ההרצה.
                </Alert>
            )}
            {plan.group && (
                <Alert severity="info" sx={{ mt: 1 }} data-testid="bulk-group-info">
                    ההקלטה המלאה של כל קבוצה נשמרת בתיקייה פרטית (<bdi dir="ltr" style={PATH_STYLE}>{groupsFolderPattern(options)}</bdi>), יחד עם רשימת המילים והזמן של כל מילה בהקלטה.
                    במסך ההרצה מאזינים לקבוצה מההתחלה ועד הסוף, ומסמנים מילה שנשמעת לא טוב.
                </Alert>
            )}
            {replacing && (
                <Alert severity="info" sx={{ mt: 1 }} data-testid="bulk-backup-info">
                    לפני כל החלפה הקובץ הישן מועתק לתיקיית הגיבוי של ההרצה (<bdi dir="ltr" style={PATH_STYLE}>{backupPatternOf(plan, options)}</bdi>), והעותק נבדק (גודל וחתימה).
                    רק אחרי זה נוצר הקובץ החדש והוא נכתב במקום הישן. ב-S3 אין גרסאות, ולכן הגיבוי הוא הדרך לחזור אחורה.
                </Alert>
            )}

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: 1.5, mt: 2 }}>
                <Stat label="ערכים שנמצאו" value={formatNumber(plan.entries.found)} note={plan.entries.notFound > 0 ? `${formatNumber(plan.entries.notFound)} מזהים לא נמצאו` : undefined} testId="bulk-stat-entries" />
                <Stat label="קבצים שנדרשים" value={formatNumber(plan.files.total)} testId="bulk-stat-total" />
                {replacing ? (
                    <>
                        <Stat label="יוחלפו (קיימים)" value={formatNumber(files)} note={plan.files.existing !== files ? `מתוך ${formatNumber(plan.files.existing)} קיימים, כל אחד אחרי גיבוי` : 'כל אחד אחרי גיבוי'} emphasis testId="bulk-stat-existing" />
                        <Stat label="חסרים" value={formatNumber(plan.files.missing)} note="לא ייווצרו במצב הזה" testId="bulk-stat-missing" />
                    </>
                ) : trial ? (
                    <>
                        <Stat label="ייווצרו לניסוי" value={formatNumber(files)} note="בתיקיית הניסוי, לא באתר" emphasis testId="bulk-stat-trial" />
                        <Stat label="כבר קיימים באתר" value={formatNumber(plan.files.existing)} note="לא ייגעו" testId="bulk-stat-existing" />
                    </>
                ) : (
                    <>
                        <Stat label="כבר קיימים" value={formatNumber(plan.files.existing)} note="לא ייגעו" testId="bulk-stat-existing" />
                        <Stat label="ייווצרו (חסרים)" value={formatNumber(files)} note={plan.files.missing !== files ? `מתוך ${formatNumber(plan.files.missing)} חסרים` : undefined} emphasis testId="bulk-stat-missing" />
                    </>
                )}
                <Stat label="תווים להקראה" value={formatNumber(plan.chars)} testId="bulk-stat-chars" />
                <Stat
                    label="עלות משוערת"
                    value={formatUsd(plan.estimatedCostUsd)}
                    note={plan.fallbackCostUsd != null ? `בגיבוי: ${formatUsd(plan.fallbackCostUsd)}` : undefined}
                    testId="bulk-stat-cost"
                />
                <Stat label="תקרת עלות" value={formatUsd(plan.maxCostUsd)} testId="bulk-stat-cap" />
                <Stat label="זמן משוער" value={formatDuration(plan.estimatedMinutes)} testId="bulk-stat-minutes" />
                {plan.group && (
                    <>
                        <Stat label="בקשות ל-Google" value={formatNumber(plan.group.requests)} note={`בכל אחת עד ${formatNumber(plan.group.size)} מילים`} emphasis testId="bulk-stat-requests" />
                        <Stat label="מילים בבקשה הגדולה" value={formatNumber(plan.group.largestRequestFiles)} testId="bulk-stat-largest" />
                        <Stat label="אורך הבקשה הארוכה (משוער)" value={formatClock(plan.group.longestRequestSeconds * 1000)} testId="bulk-stat-longest" />
                        <Stat label="מחוץ לקבוצות" value={formatNumber(plan.group.tooLong)} note={plan.group.tooLong > 0 ? 'ארוכים מדי, מכינים בהרצה בלי קבוצה' : undefined} testId="bulk-stat-too-long" />
                    </>
                )}
            </Box>

            <Typography variant="body2" sx={{ mt: 2 }} dir="auto">
                {PROVIDER_LABELS[plan.provider] ?? plan.provider}{plan.model ? ` · ${plan.model}` : ''} · {describeVoices(plan.voices)}
            </Typography>
            {plan.fallback && (
                <Typography variant="body2" color="text.secondary" dir="auto">
                    גיבוי: {PROVIDER_LABELS[plan.fallback.provider] ?? plan.fallback.provider}{plan.fallback.model ? ` · ${plan.fallback.model}` : ''} · {describeVoices(plan.fallback.voices)}
                </Typography>
            )}

            {plan.sample.length > 0 && (
                <Accordion variant="outlined" disableGutters defaultExpanded sx={{ mt: 2 }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography>הקבצים הראשונים ש{replacing ? 'יוחלפו' : 'ייווצרו'}{trial ? ' לניסוי' : ''} ({formatNumber(plan.sample.length)} מתוך {formatNumber(files)})</Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 0 }}>
                        <TableContainer sx={{ maxHeight: 360 }}>
                            <Table size="small" stickyHeader aria-label={`הקבצים הראשונים ש${replacing ? 'יוחלפו' : 'ייווצרו'}`}>
                                <TableHead>
                                    <TableRow>
                                        {['שפה', 'מגדר', 'טקסט להקראה', 'שם הקובץ'].map((header) => <TableCell key={header} sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>{header}</TableCell>)}
                                    </TableRow>
                                </TableHead>
                                <TableBody data-testid="bulk-sample">
                                    {plan.sample.map((file) => (
                                        <TableRow key={`${file.language}-${file.gender}-${file.fileName}`}>
                                            <TableCell>{languageLabel(file.language)}</TableCell>
                                            <TableCell>{GENDER_LABELS[file.gender] ?? file.gender}</TableCell>
                                            <TableCell><bdi>{file.text}</bdi></TableCell>
                                            <TableCell sx={{ fontFamily: 'monospace' }}><bdi>{file.fileName}</bdi></TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </AccordionDetails>
                </Accordion>
            )}

            {plan.skipped.count > 0 && (
                <Accordion variant="outlined" disableGutters sx={{ mt: 1 }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography>טקסטים שלא נכללו ({formatNumber(plan.skipped.count)})</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        {plan.skipped.items.map((item) => (
                            <Typography key={`${item.entryId}-${item.field}`} variant="body2" sx={{ fontFamily: 'monospace' }} dir="ltr">
                                {item.entryId} · {item.field} · {item.reason}
                            </Typography>
                        ))}
                    </AccordionDetails>
                </Accordion>
            )}

            {plan.nextAfterId && (
                <Alert severity="info" sx={{ mt: 1 }} action={<Button color="inherit" size="small" onClick={() => onContinueAfter(plan.nextAfterId)}>הכנת ההרצה הבאה</Button>}>
                    יש עוד ערכים אחרי אלה במילון. אחרי שההרצה הזאת מסתיימת אפשר להמשיך מהמזהה האחרון שנבדק.
                </Alert>
            )}

            {startError && (
                <Alert severity="error" sx={{ mt: 2 }} data-testid="bulk-start-error">
                    <AlertTitle>{startError.plan ? 'ההרצה לא התחילה, והתוכנית השתנתה' : 'ההרצה לא התחילה'}</AlertTitle>
                    {startError.plan
                        ? 'התוכנית המעודכנת מוצגת כאן. בודקים את המספרים, ואם הם מתאימים מאשרים שוב.'
                        : null}
                    <Box dir="auto">{formatError(startError)}</Box>
                </Alert>
            )}

            <Box sx={{ mt: 2, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                <Button
                    variant="contained"
                    color={replacing ? 'warning' : 'primary'}
                    disabled={Boolean(disabledReason) || starting}
                    onClick={() => setConfirming(true)}
                    data-testid="bulk-start"
                >
                    {replacing ? `החלפת ${formatNumber(files)} קבצים (עם גיבוי)` : (trial ? `ניסוי: יצירת ${formatNumber(files)} קבצים בתיקייה נפרדת` : `התחלת הרצה של ${formatNumber(files)} קבצים`)}
                </Button>
                {disabledReason && <Typography variant="body2" color="text.secondary" data-testid="bulk-start-disabled-reason">{disabledReason}</Typography>}
            </Box>

            <StartDialog plan={plan} options={options} open={confirming} starting={starting} onCancel={() => setConfirming(false)} onConfirm={confirm} />
        </Paper>
    );
};

export default BulkRunPlan;
